import { useCallback, useEffect, useState } from 'react';

import { paywallConfigApi } from '../api/services/paywallConfig';
import { track } from '../lib/analytics';
import { PAYWALL_FUNNEL } from '../types/paywall';
import type { PaywallConfig, PaywallContext, PaywallFeature } from '../types/paywall';

export interface UsePaywallConfigResult {
  config: PaywallConfig | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const exposedPaywallExperimentKeys = new Set<string>();

export const __resetPaywallExposureTrackingForTests = () => {
  exposedPaywallExperimentKeys.clear();
};

const paywallAnalyticsProperties = (data: PaywallConfig) => ({
  experiment: data.experiment,
  feature: data.feature,
  variant: data.variant,
  context: data.context,
  funnel: data.tracking.funnel,
  persona: data.tracking.persona,
  billing_actor: data.tracking.billing_actor,
  purchase_intent: data.tracking.purchase_intent,
  requires_company_context: data.tracking.requires_company_context,
  requires_card_upfront: data.tracking.requires_card_upfront,
  trial_days: data.tracking.trial_days,
  plan_count: data.plans.length,
  d0_behavior: data.d0_behavior,
  d3_channels: data.d3_channels,
  company_grace_days: data.company_grace_days,
});

/**
 * Fetch the server-driven paywall config (#251) for the given persona
 * context. Emits `paywall_config_loaded` for visibility on every successful
 * load/refetch and `experiment_exposed` exactly once per context+variant. The
 * consent gate (MR !239) wraps `track()` transversally, so this hook does not
 * duplicate any consent checks.
 */
export const usePaywallConfig = (
  context: PaywallContext,
  feature: PaywallFeature = 'hours',
  enabled = true,
): UsePaywallConfigResult => {
  const [config, setConfig] = useState<PaywallConfig | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (options?: { skipCache?: boolean }) => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const data = await paywallConfigApi.getConfig(context, { ...options, feature });
      setConfig(data);

      const analyticsProperties = paywallAnalyticsProperties(data);
      track('paywall_config_loaded', analyticsProperties);

      const exposureKey = `${data.experiment}:${data.variant}:${data.context}:${feature}`;
      if (!exposedPaywallExperimentKeys.has(exposureKey)) {
        exposedPaywallExperimentKeys.add(exposureKey);
        track('experiment_exposed', analyticsProperties);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch paywall config';
      track('paywall_config_load_failed', {
        context,
        feature,
        funnel: PAYWALL_FUNNEL,
        error: message,
      });
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [context, feature, enabled]);

  useEffect(() => {
    load();
  }, [load]);

  const refetch = useCallback(async () => {
    await load({ skipCache: true });
  }, [load]);

  return { config, isLoading, error, refetch };
};
